import { EMAIL_TEMPLATES } from 'app-constants/emails';
import { FunctionsErrorCode, httpsCallable } from 'firebase/functions';
import { functions } from 'services/Firebase';

type BaseTemplateData = {
  /** Email delivery target. */
  email: string;
  /** Base url in firebase storage for images included in email (taken from database). */
  storageUrl: string;
};

export type TemplateNameDataMap = {
  [EMAIL_TEMPLATES.creatorApprovedWithSubmissions]: BaseTemplateData & {
    /** Name of the artist. */
    name: string;
    /** Url to open when artists clicks "Get started" button (taken from database). */
    getStartedUrl: string;
    /** Content approval data. */
    submissions: Array<{
      /** Content name. */
      name: string;
      /** Content status Approved/Rejected. */
      isApproved: boolean;
      /** Content icon. */
      iconURL: string;
      /** Link to content item on jpu.app  */
      linkHref?: string;
      /** Text for linkHref's text */
      linkText?: string;
    }>;
  };
  [EMAIL_TEMPLATES.submissionMultiple]: BaseTemplateData & {
    submissions: Array<{
      /** Content name. */
      name: string;
      /** Content status Approved/Rejected. */
      isApproved: boolean;
      /** Content icon. */
      iconURL: string;
      /** Why content is rejected */
      rejectReason: string | null;
    }>;
  };
};

type ResponseError = {
  status: FunctionsErrorCode;
  message: string;
  details?: any;
};

type ResponseSuccess = true;

type ResponseData = ResponseError | ResponseSuccess;

type RequestData<
  TemplateName extends keyof TemplateNameDataMap,
  TemplateData extends TemplateNameDataMap[TemplateName],
> = {
  templateName: TemplateName;
  templateData: TemplateData;
};

/**
 * Send email cloud function callable.
 *
 * @param templateName Name of the template to use.
 * @param templateData Accompanying data for the template.
 */
export const sendEmail = async <
  T extends keyof TemplateNameDataMap,
  R extends TemplateNameDataMap[T],
>(
  templateName: T,
  templateData: R,
) => {
  const sendEmailCloudFunction = httpsCallable<RequestData<T, R>, ResponseData>(
    functions,
    'sendEmail',
  );

  return sendEmailCloudFunction({ templateName, templateData });
};
